import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { config } from '../../apps/benefit/config';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';

function extractDomain(urlString: string): string {
  try {
    const parsedUrl = new URL(urlString);
    return parsedUrl.hostname;
  } catch (error) {
    if (error instanceof Error) {
      console.warn(
        `extractDomain: invalid url '${urlString}': ${error.message}`,
      );
    } else {
      console.warn(`extractDomain: invalid url '${urlString}'`, error);
    }
    return '';
  }
}

function base64ToString(value: string): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(value, 'base64').toString('utf8');
  }

  const binString = atob(value);
  const bytes = Uint8Array.from(binString, char => char.codePointAt(0) ?? 0);
  return new TextDecoder().decode(bytes);
}

// prefix of every message sent through logTrace
export const TRACE_LOG_PREFIX = '[trace]';

export const appInsights = new ApplicationInsights({
  config: {
    connectionString: base64ToString(config.appLogCStr),
    enableAutoRouteTracking: true,
    enableCorsCorrelation: true,
    loggingLevelConsole: 2,
    distributedTracingMode: 2,
    correlationHeaderDomains: [
      extractDomain(config.benefitApiUrl),
      extractDomain(config.idpUrl),
    ],
    samplingPercentage: 20,
  },
});

appInsights.loadAppInsights();

appInsights.addTelemetryInitializer((envelope: any) => {
  envelope.tags = envelope.tags || {};
  envelope.tags['ai.cloud.role'] = 'benefit-app';
  if (process.env.NODE_ENV === 'development') {
    console.log('[AppInsights]', envelope.baseType, envelope.baseData);
  }

  const telemetryType = envelope.baseType;

  // keep every logTrace trace, they are only emitted outside PROD
  const isLogTrace =
    telemetryType === 'MessageData' &&
    envelope.baseData?.message?.startsWith(TRACE_LOG_PREFIX);

  if (
    telemetryType === 'ExceptionData' ||
    telemetryType === 'RemoteDependencyData' ||
    isLogTrace
  ) {
    envelope.sampleRate = 100;
  }
});

export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    appInsights.trackPageView({
      name: location.pathname,
      uri: globalThis.location.href,
    });
  }, [location]);
}
